import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormAutomationConfig } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type UpdateFormAutomationInput = {
  id: string;
  automation: FormAutomationConfig;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// PATCH /api/admin/forms/:id/automation
//
// Automation = fire-and-forget side effects on submit (slack, email,
// webhook). The dispatcher reads these entries; the schema only validates
// shape here. Pattern C state-flip.
export function useUpdateFormAutomation() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    UpdateFormAutomationInput,
    Context
  >({
    mutationFn: ({ id, automation }) =>
      apiFetch<{ ok: true }>(`/admin/forms/${id}/automation`, {
        method: "PATCH",
        body: JSON.stringify(automation),
      }),
    onMutate: async ({ id, automation }) => {
      const detailKey = queryKeys.adminForms.detail(id);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) =>
        old ? { ...old, automation } : old,
      );
      return { previousDetail };
    },
    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(queryKeys.adminForms.detail(id), context.previousDetail);
      }
      toast.error(err.message ?? "Failed to update automation");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminForms.detail(id) });
    },
  });
}
